import { useState, useEffect, useRef, useCallback } from 'react';

const TOKEN = 'asdknjasdfoihasldf89wr78y9r3w8e9234';
const API_BASE = 'https://api.monitgps.pl'; // Use absolute domain

export const useHazardAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastIdRef = useRef(0);
  const eventSourceRef = useRef(null);

  // 1. Fetch historical alerts (last 1 hour)
  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const url = `${API_BASE}/hazard_alerts.php?token=${TOKEN}&hours=1`;
      console.log(`[useHazardAlerts] Fetching history from: ${url}`);
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        if (data.ok && Array.isArray(data.alerts)) {
          const processed = data.alerts
            .map(a => ({
              ...a,
              latitude: Number(a.latitude),
              longitude: Number(a.longitude),
              is_active: a.is_active === "true" || a.is_active === true || a.is_active === 1 || a.is_active === "1"
            }))
            .filter(a => !isNaN(a.latitude) && !isNaN(a.longitude) && a.is_active);

          setAlerts(processed);
          const maxId = processed.reduce((max, a) => Math.max(max, Number(a.id)), 0);
          lastIdRef.current = maxId;
          console.log(`[useHazardAlerts] History loaded. Count: ${processed.length}, maxId: ${maxId}`);
        }
      }
    } catch (err) {
      console.error('[useHazardAlerts] Failed to fetch hazard history:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 2. Individual alert deactivation
  const deactivateAlert = async (id) => {
    try {
      console.log(`[useHazardAlerts] Deactivating alert ${id}...`);
      const res = await fetch(`${API_BASE}/alert_toggle.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: TOKEN,
          id: id,
          is_active: false
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (data.ok) {
          console.log(`[useHazardAlerts] Alert ${id} deactivated successfully.`);
          // Remove from local state
          setAlerts(prev => prev.filter(a => a.id !== id));
          return true;
        }
      }
      return false;
    } catch (err) {
      console.error(`[useHazardAlerts] Error deactivating alert ${id}:`, err);
      return false;
    }
  };

  // 3. Setup SSE Stream
  const connectSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const url = `${API_BASE}/sse/alerts_stream.php?token=${TOKEN}&lastId=${lastIdRef.current}`;
    console.log(`[useHazardAlerts] Connecting SSE: ${url}`);
    
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.addEventListener('connected', (e) => {
      console.log('[useHazardAlerts] SSE Connected');
      setConnected(true);
    });

    const handleAlert = (dataStr) => {
      try {
        const raw = JSON.parse(dataStr);
        const alert = {
          ...raw,
          latitude: Number(raw.latitude),
          longitude: Number(raw.longitude),
          is_active: raw.is_active === "true" || raw.is_active === true || raw.is_active === 1 || raw.is_active === "1"
        };

        if (alert.button_key === 'HAZARD_ALERT' && !isNaN(alert.latitude) && !isNaN(alert.longitude) && alert.is_active) {
          setAlerts(prev => {
            if (prev.some(a => Number(a.id) === Number(alert.id))) return prev;
            return [...prev, alert];
          });
          if (Number(alert.id) > lastIdRef.current) {
            lastIdRef.current = Number(alert.id);
          }
        }
      } catch (err) {
        console.error('[useHazardAlerts] SSE parse error:', err);
      }
    };

    es.addEventListener('alert', (e) => handleAlert(e.data));
    es.onmessage = (e) => handleAlert(e.data);

    es.addEventListener('restart', () => {
      console.log('[useHazardAlerts] SSE Restart');
      connectSSE();
    });

    es.onerror = (err) => {
      console.error('[useHazardAlerts] SSE Error:', err);
      setConnected(false);
      es.close();
      setTimeout(connectSSE, 5000);
    };
  }, []);

  useEffect(() => {
    fetchHistory().then(() => {
      connectSSE();
    });

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [fetchHistory, connectSSE]);

  return { alerts, connected, loading, deactivateAlert };
};
